/**
 * Synthesize a `GameRow` from a single `SpreadLine` / `TotalLine` so the
 * existing `GameDetailPanel` + `TradeModal` (both pinned to GameRow's
 * two-team shape) can render a single-binary-market view.
 *
 * The away/home slots both reference the SAME underlying VenueBook. The
 * panel's team selector becomes a no-op (both tabs show identical data),
 * which is benign for v1; order placement always goes to the underlying
 * ticker with the user-selected YES/NO side. Side labels carry the
 * relevant context — "Arizona Diamondbacks -5.5" / "Texas Rangers +5.5"
 * for spreads, "Over 9.5" / "Under 9.5" for totals.
 */

import type { GameEventRow, GameRow, SpreadLine, TotalLine, VenueBook } from '../ValueDashboard/types';

/**
 * D-020 LT2-005 — drop lines with no Kalshi market from the active execution
 * view (Polymarket-only rows must never consume execution space). Pass
 * `showPolyOnly: true` (the reveal toggle) to keep them; they render with
 * pills disabled either way. Returns the SAME array reference when nothing
 * filters, so memo consumers don't churn.
 */
export function executableLines<T extends { kalshi?: VenueBook }>(
  lines: T[],
  showPolyOnly: boolean,
): T[] {
  if (showPolyOnly) return lines;
  const filtered = lines.filter((l) => l.kalshi != null);
  return filtered.length === lines.length ? lines : filtered;
}

/**
 * D-020 LT2-017 — the nearest-three window around the selected line index:
 * [idx−1, idx, idx+1] clipped to bounds, always ascending (obvious ordering
 * requirement), never duplicated. At the edges the window slides rather than
 * shrinks when enough lines exist ([0,1,2] for idx 0 of 4).
 */
export function nearestLineWindow(lineCount: number, idx: number): number[] {
  if (lineCount <= 0) return [];
  const clamped = Math.min(Math.max(idx, 0), lineCount - 1);
  const size = Math.min(3, lineCount);
  let start = clamped - 1;
  if (start < 0) start = 0;
  if (start + size > lineCount) start = lineCount - size;
  return Array.from({ length: size }, (_, i) => start + i);
}

/** Event-level wrapper over `executableLines` for both strips. Same-reference
 *  when nothing filters. */
export function filterEventExecutableLines(
  event: GameEventRow,
  showPolyOnly: boolean,
): GameEventRow {
  const spreads = executableLines(event.spreads, showPolyOnly);
  const totals = executableLines(event.totals, showPolyOnly);
  if (spreads === event.spreads && totals === event.totals) return event;
  return { ...event, spreads, totals };
}

export function primaryLineBook(line: SpreadLine | TotalLine): VenueBook | null {
  return line.kalshi ?? line.polymarket ?? null;
}

/**
 * The EVENT a synthesized spread/total ticket belongs to (#754).
 *
 * The synthesized row's key is `${event.key}|spread|${lineKey}` — the grammar
 * both builders below write — so this is where it is read back, rather than in
 * a caller that would then own half of a key format it does not write. The
 * dashboard needs it to answer one question about an open ticket: is its event
 * still among the results a search or a mode admits? Returns null for a key
 * that is not one of these, which is every ordinary moneyline row.
 */
export function synthEventKey(gameKey: string): string | null {
  for (const marker of ['|spread|', '|total|']) {
    const at = gameKey.indexOf(marker);
    if (at > 0) return gameKey.slice(0, at);
  }
  return null;
}

export function spreadLineKey(line: SpreadLine): string {
  return line.kalshi?.marketTicker ?? line.polymarket?.marketTicker ?? `spread-${line.favored}-${line.line}`;
}

export function totalLineKey(line: TotalLine): string {
  return line.kalshi?.marketTicker ?? line.polymarket?.marketTicker ?? `total-${line.line}`;
}

/** Resolve a sticky selection by identity against a newly derived/reordered
 * line array. Missing identities fall back visibly to the caller's current
 * default instead of silently retaining the old array position. */
export function resolveLineIndexByKey<T>(
  lines: readonly T[],
  selectedKey: string | null | undefined,
  keyFor: (line: T) => string,
  fallbackIdx: number,
): number {
  const selectedIdx = selectedKey == null
    ? -1
    : lines.findIndex((line) => keyFor(line) === selectedKey);
  if (selectedIdx >= 0) return selectedIdx;
  if (lines.length === 0) return 0;
  return Math.min(Math.max(fallbackIdx, 0), lines.length - 1);
}

export function linePriceCents(line: SpreadLine | TotalLine): number | null {
  return primaryLineBook(line)?.priceCents ?? null;
}

/** "+3.5" / "-3.5" chip label for a signed spread line. */
export function formatSignedLine(signed: number): string {
  const sign = signed < 0 ? '-' : '+';
  return `${sign}${Math.abs(signed)}`;
}

function formatSignedMag(mag: number, sign: '+' | '−'): string {
  return `${sign}${mag}`;
}

export function synthesizeFromSpread(event: GameEventRow, line: SpreadLine): GameRow {
  const mag = Math.abs(line.line);
  const awaySign: '+' | '−' = line.favored === 'away' ? '−' : '+';
  const homeSign: '+' | '−' = line.favored === 'home' ? '−' : '+';
  return {
    key: `${event.key}|spread|${spreadLineKey(line)}`,
    sport: event.sport,
    date: event.date,
    timePt: '',
    // Carry the event's scheduled start through so the GameDetailPanel's
    // GTS / countdown / near-start-pulse logic works against synth
    // GameRows the same way it does against moneyline rows. Nulling
    // this was the cause of a "Good to Start unavailable" message
    // showing on tomorrow's CHC|HOU spread/total tickets.
    startTimeMs: event.startTimeMs,
    gameState: event.gameState, // carry the real live/pre/final state so the LIVE badge + live-order warning fire on spread/total markets too
    gameStateAuthority: event.gameStateAuthority,
    finalWinnerSide: null,
    awayCode: event.awayCode,
    homeCode: event.homeCode,
    awayName: `${event.awayName} ${formatSignedMag(mag, awaySign)}`,
    homeName: `${event.homeName} ${formatSignedMag(mag, homeSign)}`,
    kalshi: line.kalshi
      ? { away: line.kalshi, home: line.kalshi }
      : null,
    polymarket: line.polymarket
      ? { away: line.polymarket, home: line.polymarket }
      : null,
    // #555 — retain the line's authority so the order ticket's caption can
    // tell which signed name is the market's YES outcome. The duplicated
    // books above erase that; without this the caption layer can only guess.
    spreadOutcome: {
      line: line.line,
      favored: line.favored,
      kalshiTicker: line.kalshi?.marketTicker ?? null,
      polymarketTicker: line.polymarket?.marketTicker ?? null,
    },
  };
}

export function synthesizeFromTotal(event: GameEventRow, line: TotalLine): GameRow {
  return {
    key: `${event.key}|total|${totalLineKey(line)}`,
    sport: event.sport,
    date: event.date,
    timePt: '',
    startTimeMs: event.startTimeMs,
    gameState: event.gameState, // carry the real live/pre/final state so the LIVE badge + live-order warning fire on spread/total markets too
    gameStateAuthority: event.gameStateAuthority,
    finalWinnerSide: null,
    awayCode: event.awayCode,
    homeCode: event.homeCode,
    awayName: `Over ${line.line}`,
    homeName: `Under ${line.line}`,
    kalshi: line.kalshi
      ? { away: line.kalshi, home: line.kalshi }
      : null,
    polymarket: line.polymarket
      ? { away: line.polymarket, home: line.polymarket }
      : null,
  };
}
